/* eslint-disable @typescript-eslint/no-explicit-any */

import type { FeatureContext } from '../../feature-context';
import {
  analyticsReady,
  domReadyLegacy,
  globalVarReady,
} from '../../utils/ready';
import { dispatchEvent } from './dispatch-event';
import type { KioskEvent } from './types';

export type Variables = {
  kiosk?: boolean;
  timeout?: number;
};

// The one recorded subscription dispatches through whatever is here, so a
// re-install (e.g. MosaicLoader's effect re-running when the retailer changes)
// refreshes the variables/context future events are tagged with instead of
// freezing the first install's values.
let latest: { variables: Variables; context: FeatureContext } | null = null;
let subscribed = false;

/**
 * `window.vizLoader` is a stable broker facade (see catalog-ui's
 * vizLoaderBroker): it is installed early on every page and persists across
 * client-side navigations, replaying every recorded subscription onto whatever
 * loader instance is current. So a single `addListener` is all this feature
 * needs — no watching for reassignment, no re-subscribing per instance. A
 * second addListener would double-dispatch, so we subscribe exactly once and
 * read the latest variables/context at dispatch time.
 *
 * Refer to https://drive.google.com/file/d/17dzWkV1TiuRYdpwECNHoEKUsNNpqTDo6 for documentation
 */
export function install(variables: Variables, context: FeatureContext): void {
  // Always refresh what events are tagged with, even on a repeat install().
  latest = { variables, context };
  if (subscribed) return;
  subscribed = true;

  domReadyLegacy(() => {
    const eventName = variables.kiosk ? 'kioskEvents' : 'allEvents';

    // Events can only be dispatched once analytics exists; wait for it, then
    // for the loader facade, and subscribe once. If either never appears we
    // simply never subscribe, matching the previous behavior.
    analyticsReady()
      .then(() => globalVarReady('vizLoader'))
      .then((loader: any) => {
        loader.addListener(eventName, (ev: KioskEvent) => {
          const current = latest ?? { variables, context };
          dispatchEvent(ev, current.variables, current.context);
        });
      })
      .catch(() => {});
  });
}
